/**
 * Протокол WebSocket v1 (02 §5). Фаза 0: world.join, world.day, presence, tick.eta.
 *
 * Разбор входящего сообщения двухступенчатый:
 *   1) конверт (id, t, name, args) - чтобы неизвестная команда дала
 *      errors.ws.unknownCommand, а не общую ошибку разбора;
 *   2) схема аргументов конкретной команды из COMMAND_ARGS.
 * Иначе клиент получал бы "плохое сообщение" там, где команда просто не из этой фазы.
 */
import { z } from 'zod';
import { DAY_PAYLOAD_VERSION, REGIONS, WORLD_STATUSES, WORLD_TYPES } from './constants';

export const GameDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'gameDate must be YYYY-MM-DD');

/** id команды приходит от клиента и делает её идемпотентной (инвариант 8). */
export const CommandIdSchema = z.string().min(8).max(64);

export const RegionSchema = z.enum(REGIONS);
export const WorldTypeSchema = z.enum(WORLD_TYPES);
export const WorldStatusSchema = z.enum(WORLD_STATUSES);

export const WorldPublicSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  type: WorldTypeSchema,
  region: RegionSchema,
  startYear: z.number().int(),
  endYear: z.number().int(),
  tickMinutes: z.number().int().positive(),
  gameDate: GameDateSchema,
  status: WorldStatusSchema,
  players: z.number().int().nonnegative(),
  maxPlayers: z.number().int().positive(),
});

// ---------------------------------------------------------------------------
// Клиент -> сервер
// ---------------------------------------------------------------------------

export const ClientEnvelopeSchema = z.object({
  id: CommandIdSchema,
  t: z.literal('cmd'),
  name: z.string().min(1).max(64),
  args: z.unknown().optional(),
});
export type ClientEnvelope = z.infer<typeof ClientEnvelopeSchema>;

/** Аргументы команды world.join. Мир и пользователь берутся из соединения, не из тела. */
export const WorldJoinArgsSchema = z.object({}).default({});

/** Реестр команд фазы. Добавление команды = добавление схемы сюда. */
export const COMMAND_ARGS = {
  'world.join': WorldJoinArgsSchema,
} as const;

export type CommandName = keyof typeof COMMAND_ARGS;

export function isKnownCommand(name: string): name is CommandName {
  return Object.prototype.hasOwnProperty.call(COMMAND_ARGS, name);
}

// ---------------------------------------------------------------------------
// Сервер -> клиент: ответы на команды
// ---------------------------------------------------------------------------

/** gameDate живёт внутри world и намеренно не дублируется рядом: два источника правды расходятся. */
export const WorldJoinResultSchema = z.object({
  world: WorldPublicSchema,
  nextTickAt: z.number().int().nullable(),
  online: z.number().int().nonnegative(),
});
export type WorldJoinResult = z.infer<typeof WorldJoinResultSchema>;

export const AckOkSchema = z.object({
  id: CommandIdSchema,
  ok: z.literal(true),
  result: z.unknown(),
});

export const AckErrSchema = z.object({
  id: CommandIdSchema,
  ok: z.literal(false),
  code: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

export const AckSchema = z.union([AckOkSchema, AckErrSchema]);
export type Ack = z.infer<typeof AckSchema>;

// ---------------------------------------------------------------------------
// Сервер -> клиент: пуши
// ---------------------------------------------------------------------------

/**
 * Payload дня. В Фазе 0 несёт только смену даты и расписание тика;
 * рейсы добавит Фаза 2, поэтому версия payload вынесена в поле v.
 */
export const DaySchedulePayloadSchema = z.object({
  v: z.literal(DAY_PAYLOAD_VERSION),
  worldId: z.string().min(1).max(64),
  gameDate: GameDateSchema,
  /** Реальное время, когда тик состоялся. */
  realTickAt: z.number().int(),
  tickMinutes: z.number().int().positive(),
  /**
   * Абсолютное время следующего тика. null, когда мир завершён: следующего
   * тика не будет, и клиент обязан не считать обратный отсчёт до несуществующего
   * события, а показать финал.
   */
  nextTickAt: z.number().int().nullable(),
  /** Статус мира на момент тика: по нему клиент узнаёт о наступлении финала. */
  status: WorldStatusSchema,
  /** Порядковый номер тика: монотонно растёт, служит защитой от перестановки. */
  seq: z.number().int().nonnegative(),
});
export type DaySchedulePayload = z.infer<typeof DaySchedulePayloadSchema>;

export const WorldDayPushSchema = z.object({
  t: z.literal('world.day'),
  payload: DaySchedulePayloadSchema,
});

export const PresencePushSchema = z.object({
  t: z.literal('presence'),
  online: z.number().int().nonnegative(),
});

/** Приходит при join и ресинке, чтобы клиент выровнял обратный отсчёт без ожидания тика. */
export const TickEtaPushSchema = z.object({
  t: z.literal('tick.eta'),
  gameDate: GameDateSchema,
  nextTickAt: z.number().int().nullable(),
  tickMinutes: z.number().int().positive(),
});

/**
 * Ошибка уровня протокола: сообщение не разобралось настолько, что id команды
 * из него не достать. Отдельная форма нужна потому, что в ack id обязателен и
 * подчиняется CommandIdSchema - подставлять туда заглушку значит слать клиенту
 * сообщение, которое не проходит его же валидацию.
 */
export const ProtocolErrorPushSchema = z.object({
  t: z.literal('error'),
  code: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
});

export const ServerPushSchema = z.discriminatedUnion('t', [
  WorldDayPushSchema,
  PresencePushSchema,
  TickEtaPushSchema,
  ProtocolErrorPushSchema,
]);
export type ServerPush = z.infer<typeof ServerPushSchema>;

/** Всё, что клиент может получить по сокету. */
export const ServerMessageSchema = z.union([AckSchema, ServerPushSchema]);
export type ServerMessage = z.infer<typeof ServerMessageSchema>;

export function isPush(msg: ServerMessage): msg is ServerPush {
  return 't' in msg;
}
